import { useCallback } from 'react'
import type { ApolloError } from '@apollo/client'
import type {
  GetGiftRecipients,
  GetGiftRecipientsVariables,
} from '@g4g/graphql/src/shop/__generated__/GetGiftRecipients'
import { logErrorMessage } from '@g4g/utils/src/react/error'
import { useRecipientSelect, useSafeSelectOpsDispatch } from '../page/RecipientSelectProvider'
import { useCoSGiftRecipients } from '@g4g/graphql/src/shop/queries'
import { ApolloQueryResult, NetworkStatus } from '@apollo/client'

const missingDataErr = (data: GetGiftRecipients | undefined, status: NetworkStatus) => {
  if (!data && status !== NetworkStatus.ready) {
    return new Error('Failed to retrieve requested data')
  }
}

const useRefetchWrapper = (
  searchQuery: string,
  refetch: (
    variables?: Partial<GetGiftRecipientsVariables> | undefined
  ) => Promise<ApolloQueryResult<GetGiftRecipients>>
) => {
  const dispatch = useSafeSelectOpsDispatch()
  const doRefetch = useCallback(async () => {
    // Don't allow to refetch on initial render
    // or the query is not long enough
    if (!searchQuery || searchQuery.length < 2) {
      return
    }

    try {
      await refetch({ name: searchQuery })
      dispatch({ type: 'toggle-searching', searching: false })
    } catch (error) {
      logErrorMessage(error)
      dispatch({ type: 'set-search-error', error: error as ApolloError })
    }
  }, [dispatch, searchQuery, refetch])

  return doRefetch
}

const useRecipientGifters = () => {
  const { searching, searchQuery, searchError } = useRecipientSelect()
  // We want to skip when `searching: true` so we don't
  // trigger network requests on every key stroke
  const { data, loading, error, fetchMore, networkStatus, refetch } = useCoSGiftRecipients({
    name: !searching ? searchQuery : '',
    // 10 edges at a time
    // (just an expected value, check the API
    // it gets hardcoded on the backed)
    first: 10,
  })
  // Refetch has it's own state, so we need to wrap
  // it with our provider
  const doRefetch = useRefetchWrapper(searchQuery, refetch)

  return {
    // `searching: true` -> typing debounce + refetch in progress
    loading: loading || searching,
    error: error || missingDataErr(data, networkStatus) || searchError,
    empty: !data || !data.getGiftRecipients.edges || data.getGiftRecipients.edges.length === 0,
    hasNextPage: !!data?.getGiftRecipients.pageInfo.hasNextPage,
    edges: data?.getGiftRecipients.edges ?? [],
    cursor: data?.getGiftRecipients.pageInfo.endCursor,
    fetchMore,
    refetch: doRefetch,
  }
}

export default useRecipientGifters
